import React, { type ReactElement, type ReactNode } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  type RefreshControlProps,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { type WebLayout } from '../constants/layout';
import { spacing } from '../constants/spacing';
import { useAppColors } from '../context/PreferencesContext';
import {
  isWebPlatform,
  webColumnStyle,
  webMaxWidthFor,
  webScrollStyle,
  webShellStyle,
} from '../utils/webLayout';
import { isCompactWidth, webPageGutterFor } from '../utils/responsiveLayout';
import { AppLoader } from './AppLoader';

type Props = {
  children: ReactNode;
  scroll?: boolean;
  keyboardSafe?: boolean;
  loading?: boolean;
  backgroundColor?: string;
  contentStyle?: ViewStyle;
  refreshControl?: ReactElement<RefreshControlProps>;
  /**
   * Native: page / wide / fluid fill the viewport; form is a narrow auth column.
   * Web: page and wide are centered readable columns, fluid fills the viewport.
   */
  webLayout?: WebLayout;
};

export function AppScreen({
  children,
  scroll = false,
  keyboardSafe = false,
  loading = false,
  backgroundColor,
  contentStyle,
  refreshControl,
  webLayout = 'page',
}: Props) {
  const palette = useAppColors();
  const { width } = useWindowDimensions();
  const resolvedBackground = backgroundColor ?? palette.background;
  const web = isWebPlatform();
  const compactNative = !web && isCompactWidth(width);
  const columnMaxWidth = compactNative
    ? undefined
    : webMaxWidthFor(webLayout, width);
  const column = webColumnStyle(columnMaxWidth);
  /** Web: wider gutter on desktop; the scrollbar stays at the window edge. */
  const webGutter = web ? { paddingHorizontal: webPageGutterFor(width) } : null;
  const webScrollColumn = web
    ? [styles.webScrollColumn, columnMaxWidth != null ? { maxWidth: columnMaxWidth } : null]
    : null;

  const content = loading ? (
    <View style={[styles.content, styles.flex, column]}>
      <AppLoader />
    </View>
  ) : scroll ? (
    <ScrollView
      style={[styles.flex, web ? webScrollStyle() : column]}
      contentContainerStyle={[
        styles.content,
        styles.columnContent,
        webGutter,
        webScrollColumn,
        contentStyle,
      ]}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="interactive"
      automaticallyAdjustKeyboardInsets={keyboardSafe}
      refreshControl={refreshControl}
    >
      {children}
    </ScrollView>
  ) : (
    <View style={[styles.content, styles.flex, column, webGutter, contentStyle]}>
      {children}
    </View>
  );

  const body = keyboardSafe ? (
    <KeyboardAvoidingView
      style={[styles.flex, styles.fullWidth]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {content}
    </KeyboardAvoidingView>
  ) : (
    content
  );

  return (
    <SafeAreaView
      style={[
        styles.safeArea,
        web ? styles.webSafe : null,
        { backgroundColor: resolvedBackground },
      ]}
      edges={['top', 'right', 'bottom', 'left']}
    >
      <View style={webShellStyle()}>{body}</View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  webSafe: {
    overflow: 'hidden',
  },
  flex: { flex: 1, minWidth: 0 },
  fullWidth: { width: '100%', minHeight: 0 },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    maxWidth: '100%',
  },
  columnContent: {
    width: '100%',
    flexGrow: 1,
    paddingBottom: spacing.xxl,
  },
  webScrollColumn: {
    alignSelf: 'center',
    minWidth: 0,
  },
});
